// RecHairNailSalon.jsx — Actionable, client-focused Hair & Nail guide
import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18n";
import "./recommended.css";

/* Sidebar quick links */
const recommendedItems = [
  { title: "hairTrendsStylingGuide", image: "images/hair.jpg", slug: "hair-&-nail-salon" },
  { title: "mensGroomingMustacheStyles", image: "images/barbershop.jpg", slug: "barbershop" },
  { title: "tattooIdeasArtistGuide", image: "images/tattoo.jpg", slug: "tattoo-&-piercing-shops" },
  { title: "hairRemovalSkincare", image: "images/hair1.jpg", slug: "hair-removal" },
  { title: "browLashEnhancement", image: "images/brows.jpg", slug: "makeup-&-brows-&-lashes" },
  { title: "spaWellnessTreatments", image: "images/massage.jpg", slug: "massage-&-aesthetic-medicine" },
];

/* Sections with practical, immediately useful content for clients */
const sections = [
  {
    id: "face-shapes",
    titleKey: "recFaceShapeTitle",
    descriptionKey: "recFaceShapeDescription",
    readingTime: "3 min read",
    difficulty: "Beginner",
    image: "/images/hair.jpg",
    quickTips: [
      "quickTipPhoto",
      "quickTipMeasure",
      "quickTipTime"
    ],
    contentKey: "face-shapes",
  },

  {
    id: "hair-trends",
    titleKey: "recHairTrendsTitle",
    descriptionKey: "recHairTrendsDescription",
    readingTime: "4 min read",
    difficulty: "Intermediate",
    image: "/images/hair1.jpg",
    quickTips: [
      "quickTipTexturedLob",
      "quickTipCurtainBangs",
      "quickTipHeatProtectant"
    ],
    contentKey: "hair-trends",
  },

  {
    id: "color-techniques",
    titleKey: "recHairColorTitle",
    descriptionKey: "recHairColorDescription",
    readingTime: "5 min read",
    difficulty: "Intermediate",
    image: "/images/hair.jpg",
    quickTips: [
      "quickTipBalayage",
      "quickTipSulfateFree",
      "quickTipPatchTest"
    ],
    contentKey: "color-techniques",
  },

  {
    id: "nail-art",
    titleKey: "recNailCareTitle",
    descriptionKey: "recNailCareDescription",
    readingTime: "4 min read",
    difficulty: "Beginner",
    image: "/images/brows.jpg",
    quickTips: [
      "quickTipCleanNails",
      "quickTipCuticleOil",
      "quickTipLength"
    ],
    contentKey: "nail-art",
  },

  {
    id: "maintenance",
    titleKey: "recMaintenanceTitle",
    descriptionKey: "recMaintenanceDescription",
    readingTime: "3 min read",
    difficulty: "Beginner",
    image: "/images/hair1.jpg",
    quickTips: [
      "quickTipWashing",
      "quickTipSilk",
      "quickTipTrims"
    ],
    contentKey: "maintenance",
  },

  {
    id: "hair-nail-health",
    titleKey: "recHairNailHealthTitle",
    descriptionKey: "recHairNailHealthDescription",
    readingTime: "4 min read",
    difficulty: "Beginner",
    image: "/images/hair.jpg",
    quickTips: [
      "quickTipSulfateFreeHair",
      "quickTipCuticleOilNails",
      "quickTipThermalSprays"
    ],
    contentKey: "hair-nail-health",
  },
];

// Function to render section content based on contentKey
const renderSectionContent = (contentKey, t) => {
  const contentMap = {
    "face-shapes": (
      <>
        <p>{t('recFaceShapeContent1')}</p>
        <h4>{t('recFaceShapeContent2')}</h4>
        <ol>
          <li>{t('recFaceShapeContent3')}</li>
          <li>{t('recFaceShapeContent4')}</li>
          <li>{t('recFaceShapeContent5')}</li>
        </ol>
        <h4>{t('recFaceShapeContent6')}</h4>
        <ul>
          <li><strong>{t('Oval')}:</strong> {t('recFaceShapeContent7')}</li>
          <li><strong>{t('Round')}:</strong> {t('recFaceShapeContent8')}</li>
          <li><strong>{t('Square')}:</strong> {t('recFaceShapeContent9')}</li>
          <li><strong>{t('Heart')}:</strong> {t('recFaceShapeContent10')}</li>
          <li><strong>{t('Diamond')}:</strong> {t('recFaceShapeContent11')}</li>
          <li><strong>{t('Long')}:</strong> {t('recFaceShapeContent12')}</li>
        </ul>
        <h4>{t('recFaceShapeContent13')}</h4>
        <p>{t('recFaceShapeContent14')}</p>
      </>
    ),
    "hair-trends": (
      <>
        <p>{t('recHairTrendsContent1')}</p>
        <h4>{t('Trending looks and at-home approach')}</h4>
        <ul>
          <li><strong>{t('Textured lob / shag')}:</strong> {t('recHairTrendsContent3')}</li>
          <li><strong>{t('Curtain bangs')}:</strong> {t('recHairTrendsContent4')}</li>
          <li><strong>{t('Blunt bob')}:</strong> {t('recHairTrendsContent5')}</li>
          <li><strong>{t('Face-framing pieces')}:</strong> {t('recHairTrendsContent6')}</li>
        </ul>
        <h4>{t('recHairTrendsContent7')}</h4>
        <p>{t('recHairTrendsContent8')}</p>
      </>
    ),
    "color-techniques": (
      <>
        <p>{t('recHairColorContent1')}</p>
        <h4>{t('What different techniques do')}</h4>
        <ul>
          <li><strong>{t('Balayage / foilyage')}:</strong> {t('recHairColorContent3')}</li>
          <li><strong>{t('Root blending / smudge')}:</strong> {t('recHairColorContent4')}</li>
          <li><strong>{t('Face-framing highlights (money piece)')}:</strong> {t('recHairColorContent5')}</li>
          <li><strong>{t('Gloss / toner')}:</strong> {t('recHairColorContent6')}</li>
        </ul>
        <h4>{t('recHairColorContent7')}</h4>
        <ul>
          <li>{t('recHairColorContent8')}</li>
          <li>{t('recHairColorContent9')}</li>
          <li>{t('recHairColorContent10')}</li>
          <li>{t('recHairColorContent11')}</li>
        </ul>
        <h4>{t('recHairColorContent12')}</h4>
        <p>{t('recHairColorContent13')}</p>
      </>
    ),
    "nail-art": (
      <>
        <p>{t('recNailCareContent1')}</p>
        <h4>{t('recNailCareContent2')}</h4>
        <ul>
          <li>{t('recNailCareContent3')}</li>
          <li>{t('recNailCareContent4')}</li>
          <li>{t('recNailCareContent5')}</li>
        </ul>
        <h4>{t('recNailCareContent6')}</h4>
        <ul>
          <li>{t('recNailCareContent7')}</li>
          <li>{t('recNailCareContent8')}</li>
          <li>{t('recNailCareContent9')}</li>
        </ul>
        <h4>{t('recNailCareContent10')}</h4>
        <ul>
          <li>{t('recNailCareContent11')}</li>
          <li>{t('recNailCareContent12')}</li>
        </ul>
      </>
    ),
    "maintenance": (
      <>
        <p>{t('recMaintenanceContent1')}</p>
        <h4>{t('Weekly habits')}</h4>
        <ul>
          <li>{t('recMaintenanceContent3')}</li>
          <li>{t('recMaintenanceContent4')}</li>
          <li>{t('recMaintenanceContent5')}</li>
        </ul>
        <h4>{t('Monthly / every 6–10 weeks')}</h4>
        <ul>
          <li>{t('recMaintenanceContent7')}</li>
          <li>{t('recMaintenanceContent8')}</li>
          <li>{t('recMaintenanceContent9')}</li>
        </ul>
        <h4>{t('recMaintenanceContent10')}</h4>
        <ul>
          <li>{t('recMaintenanceContent11')}</li>
          <li>{t('recMaintenanceContent12')}</li>
          <li>{t('recMaintenanceContent13')}</li>
        </ul>
      </>
    ),
    "hair-nail-health": (
      <>
        <p>{t('recHairNailHealthContent1')}</p>
        <h4>{t('Daily Hair Care Routine')}</h4>
        <ul>
          <li><strong>{t('Washing Frequency')}:</strong> {t('recHairNailHealthContent3')}</li>
          <li><strong>{t('Water Temperature')}:</strong> {t('recHairNailHealthContent4')}</li>
          <li><strong>{t('Shampoo Selection')}:</strong> {t('recHairNailHealthContent5')}</li>
          <li><strong>{t('Conditioning')}:</strong> {t('recHairNailHealthContent6')}</li>
          <li><strong>{t('Drying')}:</strong> {t('recHairNailHealthContent7')}</li>
        </ul>
        <h4>{t('recHairNailHealthContent8')}</h4>
        <ul>
          <li><strong>{t('recHairNailHealthContent9')}:</strong> {t('Always apply thermal spray before using any heat tools.')}</li>
          <li><strong>{t('recHairNailHealthContent10')}:</strong> {t('Use medium heat (300-350°F) for most hair types, lower for fine hair.')}</li>
          <li><strong>{t('recHairNailHealthContent11')}:</strong> {t('Ceramic and tourmaline tools distribute heat more evenly.')}</li>
          <li><strong>{t('Frequency')}:</strong> {t('recHairNailHealthContent12')}</li>
          <li><strong>{t('recHairNailHealthContent13')}:</strong> {t('Use the cool shot button on your dryer to set styles and add shine.')}</li>
        </ul>
        <h4>{t('recHairNailHealthContent14')}</h4>
        <ul>
          <li><strong>{t('recHairNailHealthContent15')}:</strong> {t('Apply cuticle oil daily and gently push back cuticles weekly.')}</li>
          <li><strong>{t('recHairNailHealthContent16')}:</strong> {t('File nails in one direction only to prevent splitting and weakening.')}</li>
          <li><strong>{t('recHairNailHealthContent17')}:</strong> {t('Keep nails at a practical length for your lifestyle to prevent breaks.')}</li>
          <li><strong>{t('recHairNailHealthContent18')}:</strong> {t('Always use a base coat under polish to prevent staining and improve adhesion.')}</li>
          <li><strong>{t('recHairNailHealthContent19')}:</strong> {t('Apply top coat every 2-3 days to extend polish life and add shine.')}</li>
        </ul>
        <h4>{t('recHairNailHealthContent20')}</h4>
        <ul>
          <li><strong>{t('recHairNailHealthContent21')}:</strong> {t('Include lean meats, eggs, and legumes for keratin production.')}</li>
          <li><strong>{t('recHairNailHealthContent22')}:</strong> {t('Found in nuts, seeds, and whole grains - essential for hair and nail growth.')}</li>
          <li><strong>{t('recHairNailHealthContent23')}:</strong> {t('Leafy greens and red meat prevent hair loss and brittle nails.')}</li>
          <li><strong>{t('recHairNailHealthContent24')}:</strong> {t('Fish, flaxseeds, and walnuts promote scalp health and nail strength.')}</li>
          <li><strong>{t('recHairNailHealthContent25')}:</strong> {t('Drink 8-10 glasses of water daily for overall hair and nail health.')}</li>
        </ul>
        <h4>{t('recHairNailHealthContent26')}</h4>
        <ul>
          <li><strong>{t('Dry Hair')}:</strong> {t('recHairNailHealthContent27')}</li>
          <li><strong>{t('Split Ends')}:</strong> {t('recHairNailHealthContent28')}</li>
          <li><strong>{t('Brittle Nails')}:</strong> {t('recHairNailHealthContent29')}</li>
          <li><strong>{t('Color Fading')}:</strong> {t('recHairNailHealthContent30')}</li>
          <li><strong>{t('Nail Peeling')}:</strong> {t('recHairNailHealthContent31')}</li>
        </ul>
        <h4>{t('recHairNailHealthContent32')}</h4>
        <ul>
          <li><strong>{t('Hair Trims')}:</strong> {t('recHairNailHealthContent33')}</li>
          <li><strong>{t('Color Touch-ups')}:</strong> {t('recHairNailHealthContent34')}</li>
          <li><strong>{t('Deep Conditioning')}:</strong> {t('recHairNailHealthContent35')}</li>
          <li><strong>{t('Manicures')}:</strong> {t('recHairNailHealthContent36')}</li>
          <li><strong>{t('Pedicures')}:</strong> {t('recHairNailHealthContent37')}</li>
        </ul>
      </>
    ),
  };
  
  return contentMap[contentKey] || null;
};

export default function RecHairNailSalon() {
  const { t, lang } = useI18n();
  
  const handleTocClick = (e, targetId) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  return (
    <div className="rec-page" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {/* Mobile quick nav */}
      <details className="rec-mobile-panel" role="navigation" aria-label={t('recommendedServices')}>
        <summary>
          {t('recommendedServices')}
          <span className="rec-plus" aria-hidden="true">+</span>
          <span className="rec-plus" aria-hidden="true"></span>
        </summary>
        <ul className="rec-quick-list">
          {recommendedItems.map((item, i) => (
            <li key={item.slug}>
              <Link to={`/rec/${item.slug}`}>{t(item.title)}</Link>
            </li>
          ))}
        </ul>
      </details>

      {/* Sidebar (hidden on small screens via CSS) */}
      <aside className="rec-left" aria-label={t('recommendedServices')}>
        <h2 className="rec-left__title">{t('recommendedServices')}</h2>
        <ul className="rec-left__list">
          {recommendedItems.map((item, i) => (
            <li key={item.slug} className="rec-left__item">
              <Link to={`/rec/${item.slug}`} className="rec-left__link">
                <img src={`/${item.image}`} alt={t(item.title)} className="rec-left__thumb" />
                <span className="rec-left__label">{t(item.title)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </aside>

      {/* Main article */}
      <article className="rec-article">
        <header className="rec-hero">
          <h1>{t('recHairNailTitle')}</h1>
          <p className="rec-meta">{t('recHairNailSubtitle')}</p>
          <p className="rec-dek">
            {t('recHairNailDescription')}
          </p>
          <div className="rec-cta-row">
            <Link className="btn primary" to="/service/hair-&-nail-salon">{t('recFindStylist')}</Link>
            <Link className="btn outline" to="/service/hair-&-nail-salon">{t('recBookConsultation')}</Link>
          </div>
        </header>

        {/* Enhanced TOC with descriptions and metadata */}
        <nav className="rec-toc" aria-label="Table of Contents">
          <h2>{t('tableOfContents')}</h2>
          <div className="rec-toc-summary">
            <p><strong>{sections.length} {t('sections')}</strong> • <strong>{sections.reduce((total, sec) => total + parseInt(sec.readingTime), 0)} {t('minTotalRead')}</strong></p>
          </div>
          <ul className="rec-toc-list">
            {sections.map((sec, index) => (
              <li key={sec.id} className="rec-toc-item">
                <a href={`#${sec.id}`} className="rec-toc-link" onClick={(e) => handleTocClick(e, sec.id)}>
                  <div className="rec-toc-content">
                    <h3 className="rec-toc-title">{t(sec.titleKey)}</h3>
                    <p className="rec-toc-description">{t(sec.descriptionKey)}</p>
                    <div className="rec-toc-meta">
                    <span className="rec-toc-time">{sec.readingTime.replace(' min read', ` ${t('minRead')}`)}</span>
                      <span className={`rec-toc-difficulty rec-toc-difficulty--${sec.difficulty.toLowerCase()}`}>
                        {t(sec.difficulty.toLowerCase())}
                      </span>
                    </div>
                  </div>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Enhanced Sections */}
        {sections.map((sec, index) => (
          <section key={sec.id} id={sec.id} className="rec-section">
            <div className="rec-section-header">
              <div className="rec-section-meta">
                <div className="rec-section-info">
                  <h3>{t(sec.titleKey)}</h3>
                  <div className="rec-section-details">
                    <span className="rec-section-time">{sec.readingTime.replace(' min read', ` ${t('minRead')}`)}</span>
                    <span className={`rec-section-difficulty rec-section-difficulty--${sec.difficulty.toLowerCase()}`}>
                      {t(sec.difficulty.toLowerCase())}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="rec-section-content">
              <div className="rec-section-main">
                <div className="rec-section-description">
                  <p className="rec-section-intro">{t(sec.descriptionKey)}</p>
                </div>
                
                {sec.quickTips && (
                  <div className="rec-quick-tips">
                    <h4>💡 {t('quickTips')}</h4>
                    <ul className="rec-tips-list">
                      {sec.quickTips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="rec-tip-item">
                          <span className="rec-tip-text">{t(tip)}</span>
                          <span className="rec-tip-icon">✓</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="rec-section-body">
                {renderSectionContent(sec.contentKey, t)}
                </div>
              </div>
              
            </div>
          </section>
        ))}



        <footer className="rec-footer">
          <h3>{t('tableOfContents')}</h3>
          <p>{t('recPracticalAdvice')}</p>
          <div className="rec-cta-row">
            <Link className="btn primary" to="/service/hair-&-nail-salon">{t('recBookNow')}</Link>
            <Link className="btn outline" to="/service/hair-&-nail-salon">{t('recContactSalon')}</Link>
          </div>
        </footer>
      </article>
    </div>
  );
}
